import { Injectable } from '@angular/core';
import { BrandSettings } from '../models/aplicacion/brand-settings';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class BrandService {
  public readonly settings: BrandSettings = environment.BRAND;

  public initializeTheme(): void {
    if (typeof document === 'undefined') {
      return;
    }

    this.syncHeadBranding();

    const root = document.documentElement;

    this.setCssVar(root, '--color-primary', this.settings.primaryColor);
    this.setCssVar(root, '--color-primary-50', this.settings.primaryColor50 ?? `${this.settings.primaryColor}0D`);
    this.setCssVar(root, '--color-primary-100', this.settings.primaryColor100 ?? `${this.settings.primaryColor}1A`);
    this.setCssVar(root, '--color-primary-200', this.settings.primaryColor200 ?? `${this.settings.primaryColor}33`);
    this.setCssVar(root, '--color-primary-300', this.settings.primaryColor300 ?? `${this.settings.primaryColor}4D`);
    this.setCssVar(root, '--color-primary-400', this.settings.primaryColor400 ?? `${this.settings.primaryColor}66`);
    this.setCssVar(root, '--color-primary-500', this.settings.primaryColor500 ?? `${this.settings.primaryColor}80`);
    this.setCssVar(root, '--color-primary-600', this.settings.primaryColor600 ?? `${this.settings.primaryColor}99`);
    this.setCssVar(root, '--color-primary-700', this.settings.primaryColor700 ?? `${this.settings.primaryColor}B3`);
    this.setCssVar(root, '--color-primary-800', this.settings.primaryColor800 ?? `${this.settings.primaryColor}CC`);
    this.setCssVar(root, '--color-primary-900', this.settings.primaryColor900 ?? `${this.settings.primaryColor}E6`);
    this.setCssVar(root, '--color-primary-950', this.settings.primaryColor950 ?? `${this.settings.primaryColor}F2`);


    this.setCssVar(root, '--color-secondary', this.settings.secondaryColor);
    this.setCssVar(root, '--color-primary-strong', this.settings.primaryColorStrong);
    this.setCssVar(root, '--color-primary-soft', this.settings.primaryColorSoft);
    this.setCssVar(root, '--color-primary-muted', this.settings.primaryColorMuted);
    this.setCssVar(root, '--color-primary-contrast', this.settings.textOnPrimary);
    this.setCssVar(root, '--color-primary-border', `${this.settings.primaryColor}40`);
    const headerV1 = this.settings.header?.v1;
    const headerV2 = this.settings.header?.v2;
    const headerShared = this.settings.header?.shared;
    const headerSecondary = headerShared?.secondary;

    const logoIconPath = this.pickFirstText(headerV1?.logoIconPath, this.settings.logoIconPath);
    const logoWordmarkPath = this.pickFirstText(headerV1?.logoWordmarkPath, this.settings.logoWordmarkPath);
    const logoSecondaryPath = this.pickFirstText(
      headerSecondary?.logoSecondaryPath,
      this.settings.logoSecondaryPath
    );
    const logoPrimaryPath = this.pickFirstText(
      headerV2?.logoPrimaryPath,
      this.settings.logoPrimaryPath,
      logoWordmarkPath
    );

    this.setCssVar(root, '--brand-logo-icon', `url("${logoIconPath}")`);
    this.setCssVar(root, '--brand-logo-wordmark', `url("${logoWordmarkPath}")`);
    this.setCssVar(root, '--brand-logo-secondary', `url("${logoSecondaryPath}")`);
    this.setCssVar(root, '--brand-logo-primary', `url("${logoPrimaryPath}")`);

    this.setCssVar(
      root,
      '--expo-brand-icon-bg-size',
      this.resolveCssValue(
        headerV1?.logoIconBackgroundSize,
        this.resolveCssValue(this.settings.logoIconBackgroundSize, '110%')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-panel-padding',
      this.resolveCssValue(
        headerV2?.primaryPanelPadding,
        this.resolveCssValue(this.settings.logoPrimaryPanelPadding, '0')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-gap',
      this.resolveCssValue(headerV2?.primaryGap, this.resolveCssValue(this.settings.logoPrimaryGap, '0.28rem'))
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-width',
      this.resolveCssValue(headerV2?.primaryWidth, this.resolveCssValue(this.settings.logoPrimaryWidth, '100%'))
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-max-width',
      this.resolveCssValue(
        headerV2?.primaryMaxWidth,
        this.resolveCssValue(this.settings.logoPrimaryMaxWidth, '100%')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-max-height',
      this.resolveCssValue(
        headerV2?.primaryMaxHeight,
        this.resolveCssValue(this.settings.logoPrimaryMaxHeight, '3.05rem')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-max-height-mobile',
      this.resolveCssValue(
        headerV2?.primaryMaxHeightMobile,
        this.resolveCssValue(
          this.settings.logoPrimaryMaxHeightMobile,
          this.resolveCssValue(
            headerV2?.primaryMaxHeight,
            this.resolveCssValue(this.settings.logoPrimaryMaxHeight, '2.46rem')
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-object-fit',
      this.resolveCssValue(
        headerV2?.primaryObjectFit,
        this.resolveCssValue(this.settings.logoPrimaryObjectFit, 'contain')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-object-position',
      this.resolveCssValue(
        headerV2?.primaryObjectPosition,
        this.resolveCssValue(this.settings.logoPrimaryObjectPosition, 'left center')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-line-max-width',
      this.resolveCssValue(
        headerV2?.primaryLineMaxWidth,
        this.resolveCssValue(this.settings.logoPrimaryLineMaxWidth, '34ch')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-line-margin-top',
      this.resolveCssValue(
        headerV2?.primaryLineMarginTop,
        this.resolveCssValue(this.settings.logoPrimaryLineMarginTop, '0')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-line-offset-x',
      this.resolveCssValue(
        headerV2?.primaryLineOffsetX,
        this.resolveCssValue(this.settings.logoPrimaryLineOffsetX, '0')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-line-offset-y',
      this.resolveCssValue(
        headerV2?.primaryLineOffsetY,
        this.resolveCssValue(this.settings.logoPrimaryLineOffsetY, '0')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-line-font-family',
      this.resolveCssValue(
        headerV2?.primaryLineFontFamily,
        this.resolveCssValue(this.settings.logoPrimaryLineFontFamily, 'var(--brand-font-heading)')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-line-font-size',
      this.resolveCssValue(
        headerV2?.primaryLineFontSize,
        this.resolveCssValue(this.settings.logoPrimaryLineFontSize, '0.66rem')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-line-font-size-mobile',
      this.resolveCssValue(
        headerV2?.primaryLineFontSizeMobile,
        this.resolveCssValue(
          this.settings.logoPrimaryLineFontSizeMobile,
          this.resolveCssValue(
            headerV2?.primaryLineFontSize,
            this.resolveCssValue(this.settings.logoPrimaryLineFontSize, '0.57rem')
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-line-font-weight',
      this.resolveCssValue(
        headerV2?.primaryLineFontWeight,
        this.resolveCssValue(this.settings.logoPrimaryLineFontWeight, '800')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-line-letter-spacing',
      this.resolveCssValue(
        headerV2?.primaryLineLetterSpacing,
        this.resolveCssValue(this.settings.logoPrimaryLineLetterSpacing, '0.12em')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-line-line-height',
      this.resolveCssValue(
        headerV2?.primaryLineLineHeight,
        this.resolveCssValue(this.settings.logoPrimaryLineLineHeight, '1.1')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-primary-line-text-transform',
      this.resolveCssValue(
        headerV2?.primaryLineTextTransform,
        this.resolveCssValue(this.settings.logoPrimaryLineTextTransform, 'uppercase')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-inner-padding',
      this.resolveCssValue(
        headerSecondary?.cardInnerPadding,
        this.resolveCssValue(
          this.settings.logoSecondaryCardInnerPadding,
          this.resolveCssValue(
            headerSecondary?.cardPadding,
            this.resolveCssValue(this.settings.logoSecondaryCardPadding, '0.26rem 0.42rem 0.22rem 0.34rem')
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-inner-padding-mobile',
      this.resolveCssValue(
        headerSecondary?.cardInnerPaddingMobile,
        this.resolveCssValue(
          this.settings.logoSecondaryCardInnerPaddingMobile,
          this.resolveCssValue(
            headerSecondary?.cardPaddingMobile,
            this.resolveCssValue(this.settings.logoSecondaryCardPaddingMobile, '0.2rem 0.32rem 0.16rem 0.26rem')
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-outer-margin',
      this.resolveCssValue(
        headerSecondary?.cardOuterMargin,
        this.resolveCssValue(this.settings.logoSecondaryCardOuterMargin, '0 0 0 auto')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-outer-margin-mobile',
      this.resolveCssValue(
        headerSecondary?.cardOuterMarginMobile,
        this.resolveCssValue(
          this.settings.logoSecondaryCardOuterMarginMobile,
          this.resolveCssValue(
            headerSecondary?.cardOuterMargin,
            this.resolveCssValue(this.settings.logoSecondaryCardOuterMargin, '0 0 0 auto')
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-padding',
      this.resolveCssValue(
        headerSecondary?.cardPadding,
        this.resolveCssValue(this.settings.logoSecondaryCardPadding, '0.26rem 0.42rem 0.22rem 0.34rem')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-padding-mobile',
      this.resolveCssValue(
        headerSecondary?.cardPaddingMobile,
        this.resolveCssValue(this.settings.logoSecondaryCardPaddingMobile, '0.2rem 0.32rem 0.16rem 0.26rem')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-height',
      this.resolveCssValue(
        headerSecondary?.height,
        this.resolveCssValue(this.settings.logoSecondaryCardHeight, 'auto')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-height-mobile',
      this.resolveCssValue(
        headerSecondary?.heightMobile,
        this.resolveCssValue(
          this.settings.logoSecondaryCardHeightMobile,
          this.resolveCssValue(
            headerSecondary?.height,
            this.resolveCssValue(this.settings.logoSecondaryCardHeight, 'auto')
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-width-split',
      this.resolveCssValue(
        headerSecondary?.splitWidth,
        this.resolveCssValue(
          this.settings.logoSecondarySplitWidth,
          this.resolveCssValue(
            headerSecondary?.width,
            this.resolveCssValue(this.settings.logoSecondaryWidth, '100%')
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-width-split-mobile',
      this.resolveCssValue(
        headerSecondary?.splitWidthMobile,
        this.resolveCssValue(
          this.settings.logoSecondarySplitWidthMobile,
          this.resolveCssValue(
            headerSecondary?.splitWidth,
            this.resolveCssValue(
              this.settings.logoSecondarySplitWidth,
              this.resolveCssValue(
                headerSecondary?.widthMobile,
                this.resolveCssValue(this.settings.logoSecondaryWidthMobile, '100%')
              )
            )
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-height-split',
      this.resolveCssValue(
        headerSecondary?.splitHeight,
        this.resolveCssValue(
          this.settings.logoSecondarySplitHeight,
          this.resolveCssValue(
            headerSecondary?.height,
            this.resolveCssValue(this.settings.logoSecondaryCardHeight, 'auto')
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-height-split-mobile',
      this.resolveCssValue(
        headerSecondary?.splitHeightMobile,
        this.resolveCssValue(
          this.settings.logoSecondarySplitHeightMobile,
          this.resolveCssValue(
            headerSecondary?.splitHeight,
            this.resolveCssValue(
              this.settings.logoSecondarySplitHeight,
              this.resolveCssValue(
                headerSecondary?.heightMobile,
                this.resolveCssValue(this.settings.logoSecondaryCardHeightMobile, 'auto')
              )
            )
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-width',
      this.resolveCssValue(
        headerSecondary?.width,
        this.resolveCssValue(this.settings.logoSecondaryWidth, 'min(11.6rem, 36vw)')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-width-mobile',
      this.resolveCssValue(
        headerSecondary?.widthMobile,
        this.resolveCssValue(this.settings.logoSecondaryWidthMobile, 'min(7.2rem, 34vw)')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-width',
      this.resolveCssValue(
        headerSecondary?.width,
        this.resolveCssValue(this.settings.logoSecondaryWidth, 'min(11.6rem, 36vw)')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-card-width-mobile',
      this.resolveCssValue(
        headerSecondary?.widthMobile,
        this.resolveCssValue(this.settings.logoSecondaryWidthMobile, 'min(7.2rem, 34vw)')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-image-width',
      this.resolveCssValue(
        headerSecondary?.imageWidth,
        this.resolveCssValue(this.settings.logoSecondaryImageWidth, '100%')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-image-width-mobile',
      this.resolveCssValue(
        headerSecondary?.imageWidthMobile,
        this.resolveCssValue(
          this.settings.logoSecondaryImageWidthMobile,
          this.resolveCssValue(
            headerSecondary?.imageWidth,
            this.resolveCssValue(this.settings.logoSecondaryImageWidth, '100%')
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-image-height',
      this.resolveCssValue(
        headerSecondary?.imageHeight,
        this.resolveCssValue(
          this.settings.logoSecondaryImageHeight,
          this.resolveCssValue(
            headerSecondary?.maxHeight,
            this.resolveCssValue(this.settings.logoSecondaryMaxHeight, '4.5rem')
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-image-height-mobile',
      this.resolveCssValue(
        headerSecondary?.imageHeightMobile,
        this.resolveCssValue(
          this.settings.logoSecondaryImageHeightMobile,
          this.resolveCssValue(
            headerSecondary?.maxHeightMobile,
            this.resolveCssValue(
              this.settings.logoSecondaryMaxHeightMobile,
              this.resolveCssValue(
                headerSecondary?.imageHeight,
                this.resolveCssValue(this.settings.logoSecondaryImageHeight, '2rem')
              )
            )
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-max-height',
      this.resolveCssValue(
        headerSecondary?.maxHeight,
        this.resolveCssValue(this.settings.logoSecondaryMaxHeight, '4.5rem')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-max-height-mobile',
      this.resolveCssValue(
        headerSecondary?.maxHeightMobile,
        this.resolveCssValue(this.settings.logoSecondaryMaxHeightMobile, '2rem')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-offset-x',
      this.resolveCssValue(
        headerSecondary?.offsetX,
        this.resolveCssValue(this.settings.logoSecondaryOffsetX, '0rem')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-offset-x-mobile',
      this.resolveCssValue(
        headerSecondary?.offsetXMobile,
        this.resolveCssValue(
          this.settings.logoSecondaryOffsetXMobile,
          this.resolveCssValue(
            headerSecondary?.offsetX,
            this.resolveCssValue(this.settings.logoSecondaryOffsetX, '0rem')
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-object-fit',
      this.resolveCssValue(
        headerSecondary?.objectFit,
        this.resolveCssValue(this.settings.logoSecondaryObjectFit, 'contain')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-object-fit-mobile',
      this.resolveCssValue(
        headerSecondary?.objectFitMobile,
        this.resolveCssValue(
          this.settings.logoSecondaryObjectFitMobile,
          this.resolveCssValue(
            headerSecondary?.objectFit,
            this.resolveCssValue(this.settings.logoSecondaryObjectFit, 'contain')
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-object-position',
      this.resolveCssValue(
        headerSecondary?.objectPosition,
        this.resolveCssValue(this.settings.logoSecondaryObjectPosition, 'center')
      )
    );
    this.setCssVar(
      root,
      '--brand-logo-secondary-object-position-mobile',
      this.resolveCssValue(
        headerSecondary?.objectPositionMobile,
        this.resolveCssValue(
          this.settings.logoSecondaryObjectPositionMobile,
          this.resolveCssValue(
            headerSecondary?.objectPosition,
            this.resolveCssValue(this.settings.logoSecondaryObjectPosition, 'center')
          )
        )
      )
    );
    this.setCssVar(
      root,
      '--expo-brand-padding',
      this.resolveCssValue(
        headerShared?.padding,
        this.resolveCssValue(this.settings.headerBrandPadding, '0.95rem 1rem 0.85rem')
      )
    );
    this.setCssVar(
      root,
      '--expo-brand-padding-mobile',
      this.resolveCssValue(
        headerShared?.paddingMobile,
        this.resolveCssValue(
          this.settings.headerBrandPaddingMobile,
          this.resolveCssValue(headerShared?.padding, '0.95rem 1rem 0.85rem')
        )
      )
    );
    this.setCssVar(
      root,
      '--color-tap-highlight',
      this.resolveCssValue(this.settings.interactionTapHighlightColor, 'transparent')
    );
    this.setCssVar(
      root,
      '--color-focus-ring',
      this.resolveCssValue(
        this.settings.interactionFocusRingColor,
        'color-mix(in srgb, var(--color-primary) 58%, #ffffff)'
      )
    );
    this.setCssVar(
      root,
      '--color-dropdown-bg',
      this.resolveCssValue(this.settings.dropdownBackgroundColor, '#ffffff')
    );
    this.setCssVar(
      root,
      '--color-dropdown-text',
      this.resolveCssValue(this.settings.dropdownTextColor, '#1f2d41')
    );
    this.setCssVar(
      root,
      '--color-dropdown-border',
      this.resolveCssValue(this.settings.dropdownBorderColor, '#e6dfe0')
    );
    this.setCssVar(
      root,
      '--color-dropdown-icon',
      this.resolveCssValue(this.settings.dropdownIconColor, '#354862')
    );
    this.setCssVar(
      root,
      '--color-dropdown-hover-bg',
      this.resolveCssValue(this.settings.dropdownHoverBackgroundColor, '#ffffff')
    );
    this.setCssVar(
      root,
      '--color-dropdown-hover-border',
      this.resolveCssValue(
        this.settings.dropdownHoverBorderColor,
        'color-mix(in srgb, var(--color-primary) 30%, #dbe2ed)'
      )
    );
    this.setCssVar(
      root,
      '--color-dropdown-option-bg',
      this.resolveCssValue(this.settings.dropdownOptionBackgroundColor, '#ffffff')
    );
    this.setCssVar(
      root,
      '--color-dropdown-option-text',
      this.resolveCssValue(this.settings.dropdownOptionTextColor, '#1f2d41')
    );
    this.setCssVar(
      root,
      '--color-dropdown-option-hover-bg',
      this.resolveCssValue(
        this.settings.dropdownOptionHoverBackgroundColor,
        'color-mix(in srgb, var(--color-primary) 10%, #ffffff)'
      )
    );
    this.setCssVar(
      root,
      '--color-dropdown-option-selected-bg',
      this.resolveCssValue(
        this.settings.dropdownOptionSelectedBackgroundColor,
        'color-mix(in srgb, var(--color-primary) 85%, #ffffff)'
      )
    );
    this.setCssVar(
      root,
      '--color-dropdown-option-selected-text',
      this.resolveCssValue(this.settings.dropdownOptionSelectedTextColor, 'var(--color-primary-contrast)')
    );
    this.setCssVar(
      root,
      '--brand-font-base',
      this.resolveFontFamily(this.settings.fontFamilyBase, "'Segoe UI', sans-serif")
    );
    this.setCssVar(
      root,
      '--brand-font-heading',
      this.resolveFontFamily(this.settings.fontFamilyHeading, this.settings.fontFamilyBase)
    );
    this.syncFontStylesheets(this.settings.fontStylesheetUrls);
  }

  private syncHeadBranding(): void {
    const pageTitle = this.pickFirstText(
      this.settings.pageTitle,
      this.joinWithSeparator(' | ', this.settings.appName, this.settings.name),
      this.settings.appName,
      this.settings.name
    );
    const metaDescription = this.pickFirstText(this.settings.metaDescription, this.settings.tagline);
    const themeColor = this.pickFirstText(this.settings.themeColor, this.settings.primaryColor);

    this.setDocumentTitle(pageTitle);
    this.upsertMetaByName('description', metaDescription);
    this.upsertMetaByName('theme-color', themeColor);
    this.upsertLink('icon', this.pickFirstText(this.settings.faviconPath, 'favicon.ico'), {
      type: 'image/x-icon'
    });
    this.upsertLink(
      'apple-touch-icon',
      this.pickFirstText(this.settings.appleTouchIconPath, this.settings.ogImagePath),
      { sizes: '180x180' }
    );
    this.upsertLink('manifest', this.pickFirstText(this.settings.manifestPath, 'manifest.webmanifest'));

    const ogTitle = this.pickFirstText(this.settings.ogTitle, pageTitle);
    const ogDescription = this.pickFirstText(this.settings.ogDescription, metaDescription);
    const ogSiteName = this.pickFirstText(this.settings.ogSiteName, this.settings.name);
    const ogImage = this.toAbsoluteUrl(this.pickFirstText(this.settings.ogImagePath, this.settings.appleTouchIconPath));
    const ogUrl = this.toAbsoluteUrl(this.pickFirstText(this.settings.ogUrl, '/'));

    this.upsertMetaByProperty('og:type', 'website');
    this.upsertMetaByProperty('og:site_name', ogSiteName);
    this.upsertMetaByProperty('og:title', ogTitle);
    this.upsertMetaByProperty('og:description', ogDescription);
    this.upsertMetaByProperty('og:image', ogImage);
    this.upsertMetaByProperty('og:url', ogUrl);

    const twitterCard = this.pickFirstText(this.settings.twitterCard, 'summary_large_image');
    const twitterTitle = this.pickFirstText(this.settings.twitterTitle, ogTitle);
    const twitterDescription = this.pickFirstText(this.settings.twitterDescription, ogDescription);
    const twitterImage = this.toAbsoluteUrl(
      this.pickFirstText(this.settings.twitterImagePath, this.settings.ogImagePath, this.settings.appleTouchIconPath)
    );

    this.upsertMetaByName('twitter:card', twitterCard);
    this.upsertMetaByName('twitter:title', twitterTitle);
    this.upsertMetaByName('twitter:description', twitterDescription);
    this.upsertMetaByName('twitter:image', twitterImage);
  }

  private setDocumentTitle(title: string): void {
    const normalized = this.normalizeText(title);
    if (!normalized) {
      return;
    }
    document.title = normalized;
  }

  private upsertMetaByName(name: string, content: string): void {
    this.upsertMeta('name', name, content);
  }

  private upsertMetaByProperty(property: string, content: string): void {
    this.upsertMeta('property', property, content);
  }

  private upsertMeta(attributeName: 'name' | 'property', attributeValue: string, content: string): void {
    const head = document.head;
    const key = this.normalizeText(attributeValue);
    const normalizedContent = this.normalizeText(content);
    if (!head || !key || !normalizedContent) {
      return;
    }

    const selector = `meta[${attributeName}="${key}"]`;
    const existing = head.querySelector(selector) as HTMLMetaElement | null;
    if (existing) {
      existing.content = normalizedContent;
      return;
    }

    const meta = document.createElement('meta');
    meta.setAttribute(attributeName, key);
    meta.content = normalizedContent;
    head.appendChild(meta);
  }

  private upsertLink(rel: string, href: string, attributes?: Record<string, string>): void {
    const head = document.head;
    const normalizedRel = this.normalizeText(rel);
    const normalizedHref = this.normalizeText(href);
    if (!head || !normalizedRel || !normalizedHref) {
      return;
    }

    const selector = `link[rel="${normalizedRel}"]`;
    const existing = head.querySelector(selector) as HTMLLinkElement | null;
    const link = existing ?? document.createElement('link');
    link.rel = normalizedRel;
    link.href = normalizedHref;

    if (attributes) {
      Object.entries(attributes).forEach(([name, value]) => {
        if (!this.normalizeText(value)) {
          return;
        }
        link.setAttribute(name, value);
      });
    }

    if (!existing) {
      head.appendChild(link);
    }
  }

  private toAbsoluteUrl(path: string): string {
    const normalizedPath = this.normalizeText(path);
    if (!normalizedPath) {
      return '';
    }
    if (/^[a-z]+:\/\//i.test(normalizedPath)) {
      return normalizedPath;
    }
    if (normalizedPath.startsWith('//')) {
      return `${window.location.protocol}${normalizedPath}`;
    }

    try {
      return new URL(normalizedPath, document.baseURI).toString();
    } catch {
      return normalizedPath;
    }
  }

  private pickFirstText(...values: Array<string | null | undefined>): string {
    for (const value of values) {
      const normalized = this.normalizeText(value);
      if (normalized) {
        return normalized;
      }
    }
    return '';
  }

  private joinWithSeparator(separator: string, ...parts: Array<string | null | undefined>): string {
    const normalizedParts = parts
      .map((part) => this.normalizeText(part))
      .filter((part) => part.length > 0);
    return normalizedParts.join(separator);
  }

  private setCssVar(root: HTMLElement, varName: string, value: string): void {
    root.style.setProperty(varName, value);
  }

  private resolveFontFamily(value: string, fallback: string): string {
    const normalized = this.normalizeText(value);
    if (normalized) {
      return normalized;
    }
    return this.normalizeText(fallback) || "'Segoe UI', sans-serif";
  }

  private resolveCssValue(value: string | null | undefined, fallback: string): string {
    const normalized = this.normalizeText(value);
    if (normalized) {
      return normalized;
    }
    return fallback;
  }

  private syncFontStylesheets(urls: string[]): void {
    const head = document.head;
    if (!head) {
      return;
    }

    head.querySelectorAll('link[data-brand-font="true"]').forEach((node) => {
      node.parentElement?.removeChild(node);
    });

    this.asArray(urls)
      .map((url) => this.normalizeText(url))
      .filter((url) => url.length > 0)
      .forEach((href) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.setAttribute('data-brand-font', 'true');
        head.appendChild(link);
      });
  }

  private asArray<T>(value: T[] | null | undefined): T[] {
    return Array.isArray(value) ? value : [];
  }

  private normalizeText(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }
    return value.trim();
  }
}
